/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { ReactElement, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import Button from '../controls/Button';
import { useData } from '../context/data.context';
import MultiSelectDropdown from '../controls/MultiSelectDropDown';
import CustomDatePicker from '../controls/CustomDatePicker';
import {
  downloadS3File,
  getScalingCalculations,
  uploadS3File
} from '../services/dashboard-service';
import SampleData from '../sample.json';
import Graph from '../components/histogram/Graph';
import Deque from '../classes/deque';
import { changeZeroValuesToNull } from '../_helpers/globalHelper';
import { DeviceType, ParametersType } from '../constants/telosair-interfaces';
import CalibrationScatterChart from '../components/CalibrationScatter';
import { downloadFile } from '../_helpers/dashboardHelper';

type CalibrationData = {
  detailed: { data: number[][]; name: string; actualSensor: string }[];
  linearFitData: any;
};

type FORMDATA = {
  referenceSensors: {
    label: string;
    value: string;
  }[];
  sensorsToCalibrate: {
    label: string;
    value: string;
  }[];
  parameters: {
    label: string;
    value: string;
  }[];
  startTime: Date;
  endTime: Date;
  referenceFile?: FileList;
};

/**
 * Page component for device calibration.
 * @returns React Element.
 */
export default function CalibrationPage(): ReactElement {
  const { devices: deviceList, parameters } = useData();
  const [loading, setLoading] = useState(false);
  const [uploadMode, setUploadMode] = useState<'sensor' | 'file'>('sensor');
  const [calibrationData, setCalibrationData] = useState<Record<string, CalibrationData>>({});
  const [lineChartGraphData, setLineChartGraphData] = useState<
    Record<string, Record<string, Deque>>
  >({});
  const [lineChartLabels, setLineChartLabels] = useState<Record<string, Record<string, string>>>(
    {}
  );
  const methods = useForm<FORMDATA>({
    mode: 'all',
    defaultValues: {
      startTime: new Date(),
      endTime: new Date()
    }
  });

  const {
    handleSubmit,
    control,
    register,
    watch,
    formState: { errors }
  } = methods;

  const parameterList = parameters.map((item) => ({
    label: item.displayName,
    value: item.parameterName
  }));

  const deviceOptions = deviceList.map((device) => ({
    label: device.deviceName || device.serialNumber.toString(),
    value: device.serialNumber.toString()
  }));

  /**
   * Handles form submission for saving calibration.
   * @param formData - Form data for calibration.
   */
  const onSave = async (formData: FORMDATA): Promise<void> => {
    try {
      setLoading(true);
      // TODO: Implement save functionality
      // eslint-disable-next-line no-console
      console.log('Saving calibration:', formData);
      toast.success('Calibration saved successfully');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error('Failed to save calibration');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles saving of calibration.
   * @param formData - Form data for calibration.
   */
  const onCalculate = async (formData: FORMDATA): Promise<void> => {
    try {
      setLoading(true);

      if (uploadMode === 'file' && formData.referenceFile?.[0]) {
        await uploadS3File(formData.referenceFile[0]);
      }

      const parameterPromises = formData.parameters.map((param) =>
        getScalingCalculations({
          parameter: param.value,
          toSensor: formData.sensorsToCalibrate.map((item) => item.value).join(','),
          fromSensors: formData.referenceSensors.map((item) => item.value).join(','),
          startDate: formData.startTime.getTime(),
          endDate: formData.endTime.getTime()
        })
      );

      const results = await Promise.allSettled(parameterPromises);

      const newCalibrationData: Record<string, CalibrationData> = {};

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const paramValue = formData.parameters[index].value;
          newCalibrationData[paramValue] = SampleData; // Replace with actual API response when available
        } else {
          toast.error(`Failed to calculate for parameter: ${formData.parameters[index].label}`);
        }
      });

      setCalibrationData(newCalibrationData);
      toast.success('Calculations completed successfully');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error('Failed to calculate');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Resets the form to initial state.
   */
  const onReset = (): void => {
    methods.reset();
    setUploadMode('sensor');
    setCalibrationData({});
    setLineChartGraphData({});
    setLineChartLabels({});
  };

  /**
   * Navigates to existing calibrations view.
   */
  const onViewExisting = (): void => {
    // TODO: Implement view existing calibrations
  };

  const selectedParameters = watch('parameters') || [];

  useEffect(() => {
    if (!calibrationData || Object.keys(calibrationData).length === 0) return;

    const newGraphData: Record<string, Record<string, Deque>> = {};
    const newLabels: Record<string, Record<string, string>> = {};

    Object.entries(calibrationData).forEach(([paramKey, paramData]) => {
      if (paramData?.detailed) {
        newGraphData[paramKey] = {};
        newLabels[paramKey] = {};

        paramData.detailed.forEach((item) => {
          const value = changeZeroValuesToNull(item.data);
          const dequeData = new Deque();
          dequeData.add(...value);

          newGraphData[paramKey][item.name] = dequeData;
          newLabels[paramKey][item.name] = item.name.includes('ref_sensor')
            ? `REF ${item.actualSensor}`
            : `CAL ${item.actualSensor}`;
        });
      }
    });

    setLineChartGraphData(newGraphData);
    setLineChartLabels(newLabels);
  }, [calibrationData]);

  /**
   * Download sample CSV.
   */
  const downloadSampleCSV = async (): Promise<void> => {
    try {
      const response = await downloadS3File(
        '91011567-360e-4c36-a5e9-e3ee6b70be20_calibarationFileUpload.csv'
      );
      // Handle blob response - response.data should be the blob
      let blob: Blob;
      if (response.data instanceof Blob) {
        blob = response.data;
      } else if (typeof response.data === 'string') {
        blob = new Blob([response.data], { type: 'text/csv' });
      } else {
        blob = new Blob([response.data]);
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sample.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('File downloaded successfully');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error('File download failed');
    }
  };

  return (
    <div className="p-6 bg-container-500 my-4 flex flex-1 flex-col rounded text-white">
      <h1 className="text-2xl font-bold">Calibrate devices</h1>
      <div className="h-[1px] w-full bg-gray-900 my-6" />
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(onCalculate)} className="flex flex-col flex-1">
          <div>
            <div className="flex gap-4 mb-4">
              <Button
                theme={uploadMode === 'file' ? 'secondary-color' : 'primary'}
                size="sm"
                onClick={() => setUploadMode('sensor')}
                name="Use Reference Sensors"
              />
              <Button
                theme={uploadMode === 'file' ? 'primary' : 'secondary-color'}
                size="sm"
                name="Upload File"
                onClick={() => setUploadMode('file')}
              />
            </div>

            {uploadMode === 'sensor' ? (
              <MultiSelectDropdown
                id="referenceSensors"
                label="Reference Sensors"
                options={deviceOptions}
                validationObject={{ required: 'Please select at least one reference sensor' }}
                control={control}
              />
            ) : (
              <div className="flex flex-col">
                <div className="text-sm font-medium flex flex-col">
                  <div className="flex justify-between">
                    <div className="text-gray-500 dark:text-gray-500 text-sm">
                      Upload Reference Data (CSV) *
                    </div>
                    <button
                      type="button"
                      className="text-primary-500 underline text-smtext-sm"
                      onClick={downloadSampleCSV}
                    >
                      Download Sample CSV
                    </button>
                  </div>
                  <input
                    id="referenceFile"
                    type="file"
                    accept=".csv"
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...register('referenceFile', {
                      required: 'Please upload a CSV file',
                      // eslint-disable-next-line jsdoc/require-jsdoc
                      validate: (files) => {
                        if (!files || files.length === 0) return 'Please upload a file';
                        const file = files[0];
                        if (file.type !== 'text/csv') return 'Please upload a CSV file';
                        return true;
                      }
                    })}
                    className={`w-full block py-1.5 px-3.5 text-base font-normal dark:text-gray-300 placeholder-gray-400 border rounded bg-gray-50 dark:bg-container-500 focus:dark:border-primary-300 focus-within:dark:ring-transparent mt-2 ${
                      errors.referenceFile ? 'border-error-300' : 'border-gray-700'
                    }`}
                  />
                </div>
                {errors.referenceFile && (
                  <p className="text-error-500 mt-1">{errors.referenceFile.message}</p>
                )}
              </div>
            )}

            <div className="flex gap-4">
              <MultiSelectDropdown
                id="sensorsToCalibrate"
                label="Sensors to Calibrate"
                options={deviceOptions}
                validationObject={{ required: 'Please select at least one sensor to calibrate' }}
                control={control}
              />

              <MultiSelectDropdown
                id="parameters"
                label="Parameters"
                control={control}
                options={parameterList}
                validationObject={{ required: 'Please select at least one parameter' }}
              />
            </div>

            <div className="flex gap-4">
              <CustomDatePicker
                label="Start Time"
                id="startTime"
                validationObject={{ required: 'Please enter the start time' }}
                control={control}
                defaultValue={methods.getValues('startTime')}
              />
              <CustomDatePicker
                label="End Time"
                id="endTime"
                validationObject={{ required: 'Please enter the end time' }}
                control={control}
                defaultValue={methods.getValues('endTime')}
              />
            </div>
          </div>

          <div className="flex gap-4 justify-end mt-10">
            <Button theme="secondary-error" size="sm" name="Reset" onClick={onReset} />
            <Button
              theme="secondary-color"
              size="sm"
              name="Existing Calibrations"
              onClick={onViewExisting}
            />
            <Button theme="secondary-color" onClick={onSave} size="sm" name="Save" />
            <Button
              theme="primary"
              size="sm"
              loading={loading}
              name="Calculate"
              buttonType="submit"
            />
          </div>
        </form>
      </FormProvider>
      <div>
        <div className="flex flex-1 flex-col gap-4">
          {selectedParameters.map(
            (param) =>
              lineChartGraphData[param.value] && (
                <div key={param.value} className="flex flex-col flex-1 mt-4">
                  <h3 className="text-lg font-semibold mb-2">{param.label}</h3>
                  <div className="flex flex-1 justify-between gap-3">
                    <div className="flex-1 flex">
                      <Graph<string>
                        labels={lineChartLabels[param.value] || {}}
                        sameYaxis
                        useDynamicLineColorPlugin={false}
                        useTextPointPlugin={false}
                        lineChartGraphData={lineChartGraphData[param.value] || {}}
                        type={DeviceType.MIXED}
                      />
                    </div>
                    <div className="flex-1 flex">
                      <CalibrationScatterChart
                        data={{
                          linearFitData: calibrationData[param.value].linearFitData || {}
                        }}
                      />
                    </div>
                  </div>
                </div>
              )
          )}
        </div>
      </div>
    </div>
  );
}
